"use client";

import { useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import { isSupportedLocale } from "@/lib/i18n/locales";

const LANDING_HASHES = new Set(["#how-it-works", "#growth", "#pricing"]);

/**
 * Handles in-page anchors (#how-it-works, #growth, #pricing legacy).
 * Next.js <Link href="/#how-it-works"> often fails to scroll when already on /
 * or when a sticky header + smooth scroll intercept the jump — first click
 * only updates the hash; second click used to be required. We intercept
 * clicks and force a single-click scroll.
 */
export function HashScroller() {
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    const localeMatch = pathname.match(/^\/([a-z]{2})(?=\/|$)/);
    const locale =
      localeMatch &&
      isSupportedLocale(localeMatch[1]) &&
      localeMatch[1] !== "en"
        ? localeMatch[1]
        : null;
    const homePath = locale ? `/${locale}` : "/";

    const scrollToHash = (explicitHash?: string) => {
      const hash = explicitHash ?? window.location.hash;
      if (!hash || hash.length < 2) return;

      // Legacy: full plans live on /pricing, not on the home grid
      if (hash === "#pricing") {
        const pricingPath = locale ? `/${locale}/pricing` : "/pricing";
        if (pathname !== pricingPath && pathname !== "/pricing") {
          router.replace(pricingPath);
        } else {
          const el = document.getElementById("pricing");
          el?.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        return;
      }

      // Landing hashes only exist on the landing page; preserve current locale prefix
      if (
        LANDING_HASHES.has(hash) &&
        pathname !== homePath &&
        pathname !== "/"
      ) {
        router.replace(locale ? `/${locale}${hash}` : `/${hash}`);
        return;
      }

      const id = hash.slice(1);
      const tryScroll = (attempt = 0) => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          return;
        }
        // Section may still be hydrating (dynamic import)
        if (attempt < 12) {
          window.setTimeout(() => tryScroll(attempt + 1), 80);
        }
      };
      // After paint
      window.requestAnimationFrame(() => tryScroll());
    };

    // Initial + delayed (dynamic Features section)
    scrollToHash();
    const t1 = window.setTimeout(() => scrollToHash(), 120);
    const t2 = window.setTimeout(() => scrollToHash(), 400);

    const onHashChange = () => scrollToHash();
    window.addEventListener("hashchange", onHashChange);

    /**
     * Single-click fix: when already on `/` (or any page), a Link to
     * `/#how-it-works` may not fire hashchange if the hash was already partially
     * applied, or Next soft-nav may skip the first scroll. Intercept
     * same-document hash links.
     */
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
        return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest("a[href]");
      if (!(anchor instanceof HTMLAnchorElement)) return;
      const href = anchor.getAttribute("href");
      if (!href) return;

      let url: URL;
      try {
        url = new URL(href, window.location.origin);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;
      const hash = url.hash;
      if (hash !== "#how-it-works" && hash !== "#growth" && hash !== "#pricing")
        return;

      // Same-page landing anchors: scroll immediately without requiring a second click.
      const targetPath = url.pathname.replace(/\/$/, "") || "/";
      const currentPath = pathname.replace(/\/$/, "") || "/";

      if (hash === "#pricing") {
        const pricingPath = locale ? `/${locale}/pricing` : "/pricing";
        if (
          currentPath === "/pricing" ||
          targetPath === "/pricing" ||
          currentPath === pricingPath ||
          targetPath === pricingPath
        ) {
          event.preventDefault();
          if (window.location.hash !== hash) {
            window.history.pushState(
              null,
              "",
              `${window.location.pathname}${hash}`,
            );
          }
          scrollToHash(hash);
        }
        return;
      }

      const isTargetHome =
        targetPath === "/" ||
        targetPath === "" ||
        (Boolean(locale) && targetPath === `/${locale}`);

      if (LANDING_HASHES.has(hash) && isTargetHome) {
        if (
          currentPath === "/" ||
          (Boolean(locale) && currentPath === `/${locale}`)
        ) {
          event.preventDefault();
          if (window.location.hash !== hash) {
            window.history.pushState(
              null,
              "",
              locale && currentPath === `/${locale}`
                ? `/${locale}${hash}`
                : `/${hash}`,
            );
          }
          scrollToHash(hash);
        }
        // Off-home navigations: let Link work; effect re-runs on pathname change.
      }
    };

    document.addEventListener("click", onClick, true);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.removeEventListener("hashchange", onHashChange);
      document.removeEventListener("click", onClick, true);
    };
  }, [pathname, router]);

  return null;
}
